/**
 * Base class which provides basic functions for any view intended to be displayed as a window pane in a window associated with a `MapIndex`. Each `MapDetailView` consists of a title, a footer (optional), and a set of controls which are expected to be placed in a toolbar.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * controls-changed | None. | Fires whenever the set of controls is modified.
 * title-changed | None. | Fires whenever the title for this view is set.
 * footer-changed | None. | Fires whenever the footer setting for this view is updated.
 * @module app/views/map/MapDetailView
 */
import Backbone from "backbone";
import $ from "jquery";
import _ from "underscore";
import ErrorUtils from "app/utils/ErrorUtils";
import $bootstrap from "bootstrap";
import _remove from "lodash.remove";
export default Backbone.View.extend({
  initialize: function (options) {
    Backbone.View.prototype.initialize.apply(this, options);

    this._controls = [];
    this._title = "Properties";
    this._footer = false;
  },
  /**
   * Utility function for creating an icon button from a `BoundOperation`.
   * @param  {BoundOperation} operation The operation supplying button data.
   * @return {jquery}         A jquery object containing the control that was created. If the operation is unavailable, this will be an empty jquery object.
   */
  createIconButton: function (operation) {
    var text = operation.get("text");
    var icon = operation.get("icon");
    var click = function () {
      _.defer(function () {
        operation.action().fail(ErrorUtils.errorHandler());
      });
    };

    var glyph = $('<span class="glyphicon glyphicon-' + icon + '"></span>');
    var result = $();

    if (text) {
      $bootstrap(glyph).tooltip({
        title: text,
      });
    }

    glyph.click(_.bind(click, this));

    if (operation.available()) {
      result = result.add(glyph);
    }

    return result;
  },

  /**
   * Gets the set of controls associated with this view.
   * @return {Array} An array of `jquery` objects containing the controls.
   */
  getControls: function () {
    return this._controls;
  },

  /**
   * Adds a control to the set of controls.
   * @param {jquery} control The control to add.
   */
  addControl: function (control) {
    this._controls.push(control);
    this.trigger("controls-changed");
  },

  /**
   * Removes a control from the set of controls.
   * @param  {jquery} control The control to remove.
   */
  removeControl: function (control) {
    _remove(this._controls, control);
    this.trigger("controls-changed");
  },

  /**
   * Removes all controls from the set of controls.
   */
  clearControls: function () {
    this._controls = [];
    this.trigger("controls-cleared");
    this.trigger("controls-changed");
  },

  /**
   * Refreshes the controls in the detail view.
   */
  refreshControls: function () {},

  /**
   * Gets a title to be used to label the view.
   * @return {string} The title.
   */
  getTitle: function () {
    return this._title;
  },

  /**
   * Sets the title to be used to label the view.
   * @param {string} title The title.
   */
  setTitle: function (title) {
    this._title = title;
    this.trigger("title-changed");
  },

  /**
   * Gets a footer element to be loaded into a footer container.
   * @return {jquery|Boolean} A jquery object containing the elements to place in the footer, or `false` to indicate that no footer shall be used.
   */
  getFooter: function () {
    return this._footer;
  },

  /**
   * Sets the footer associated with this view.
   * @param {jquery|Boolean} footer The object containing the elements to place in the footer, or `false` to indicate that no footer shall be used.
   */
  setFooter: function (footer) {
    this._footer = footer;
    this.trigger("footer-changed");
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/MapDetailView.js
// module id = 172
// module chunks = 1